import { useMemo, useCallback } from 'react';
import useStyles from './styles';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Instagram from '@mui/icons-material/Instagram';
import YouTube from '@mui/icons-material/YouTube';
import * as IconMDI from 'mdi-material-ui';
import { useAuth, formatPrice, cardSx } from '@elevator/ui';
import { SubmissionTypeInfo } from 'types/submissions';
import clsx from 'clsx';

/**
 * ServiceCard component displays a card for a submission type option.
 * Shows platform icon, service name, price, and description. Highlights when selected.
 * Used in ServiceStep to allow users to select a submission type.
 *
 * @param props - Component props
 * @param props.service - Submission type service information
 * @param props.selected - Whether this card is currently selected
 * @returns ServiceCard component
 */
const ServiceCard = ({ service, selected }: ServiceCardProps) => {
  const { classes } = useStyles();
  const { currentUser } = useAuth();

  const getIcon = useCallback(
    (platform: string) => {
      switch (platform) {
        case 'instagram':
          return <Instagram className={classes.instagram} />;
        case 'youtube':
          return <YouTube className={classes.youtube} />;
        case 'elevator':
          return <IconMDI.MonitorDashboard className={classes.desktopMac} />;
        default:
          return <IconMDI.HelpBox />;
      }
    },
    [classes.instagram, classes.youtube, classes.desktopMac],
  );

  // Memoize icon since platform rarely changes
  const icon = useMemo(
    () => getIcon(service.platform || ''),
    [getIcon, service.platform],
  );

  const displayPrice =
    service.name === 'Standard Submission' && currentUser && currentUser.preferred
      ? 'Free'
      : `$${formatPrice(service.cost)}`;

  return (
    <Paper
      square
      elevation={1}
      sx={cardSx}
      className={clsx(selected ? classes.activeRoot : classes.root)}
      role="option"
      aria-selected={selected}
      data-component="ServiceCard"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        className={classes.titlePrice}
      >
        <Stack direction="row" alignItems="center" className={classes.titleAndIcon}>
          {icon}
          <Typography variant="subtitle2" className={classes.title}>
            {service.name}
          </Typography>
        </Stack>
        <Typography variant="subtitle2">{displayPrice}</Typography>
      </Stack>
      <Typography variant="overline2" className={classes.description}>
        {service.description}
      </Typography>
    </Paper>
  );
};

export type ServiceCardProps = {
  /** Submission type service information */
  service: SubmissionTypeInfo;
  /** Whether this card is currently selected */
  selected: boolean;
};

export default ServiceCard;
